export interface KeyboardHandler {
  (event: KeyboardEvent): void | boolean;
}

export type KeyboardPhase = 'capture' | 'bubble';

export interface ContextHandlers {
  [key: string]: KeyboardHandler;
}

export interface ContextData {
  handlers: ContextHandlers;
  registrationOrder: number;
  focus?: string;
  phase: KeyboardPhase;
}

export interface KeyboardControllerConfig {
  handlers?: ContextHandlers;
  focus?: string;
  phase?: KeyboardPhase;
}

export interface KeyboardControllerInstance {
  registerHandlers: (context: string, config: KeyboardControllerConfig) => void;
  unregister: (context: string) => void;
  focus: (context?: string) => void;
  clear: () => void;
  getFocus: (context: string) => (selector: string) => void;
  (context: string, config: KeyboardControllerConfig): void;
}

let $keyboard: KeyboardControllerInstance;

const createKeyboardController = () => {
  const contexts = new Map<string, ContextData>();
  const activeContexts = new Set<string>();
  const focusAttempts = new Map<string, number>();
  let registrationCounter = 0;
  const MAX_FOCUS_ATTEMPTS = 10;
  const FOCUS_RETRY_DELAY = 300;

  const attemptFocus = (selector: string): boolean => {
    const element = document.querySelector(selector) as HTMLElement;
    if (!element) return false;

    document.activeElement instanceof HTMLElement && document.activeElement.blur();
    element.focus();
    return document.activeElement === element;
  };

  const focusElement = (context: string, selector: string): void => {
    if (!activeContexts.has(context)) return;

    const attempts = focusAttempts.get(selector) || 0;
    if (attempts >= MAX_FOCUS_ATTEMPTS) {
      focusAttempts.delete(selector);
      console.warn(`Failed to focus element "${selector}" after ${MAX_FOCUS_ATTEMPTS} attempts`);
      return;
    }

    if (attemptFocus(selector)) {
      focusAttempts.delete(selector);
      return;
    }

    focusAttempts.set(selector, attempts + 1);
    setTimeout(() => activeContexts.has(context) ? focusElement(context, selector) : focusAttempts.delete(selector), FOCUS_RETRY_DELAY);
  };

  const isContextMapItem = (context: any): context is { contextId: string, data: ContextData } => context.data !== undefined;

  const orderedContexts = (): ContextData[] => Array.from(activeContexts)
    .map(contextId => ({ contextId, data: contexts.get(contextId) }))
    .filter(isContextMapItem)
    .sort((a, b) => b.data.registrationOrder - a.data.registrationOrder)
    .map(({ data }) => data);

  const handlerFor = (data: ContextData, event: KeyboardEvent): KeyboardHandler | undefined => {
    const modifiers = `${event.ctrlKey ? 'Ctrl+' : ''}${event.shiftKey ? 'Shift+' : ''}${event.altKey ? 'Alt+' : ''}`;
    return data.handlers[`${modifiers}${event.key}`] || data.handlers[`${modifiers}${event.keyCode}`];
  };

  const dispatch = (event: KeyboardEvent, phase: KeyboardPhase, ordered: ContextData[]) => {
    ordered.some((data) => {
      const handler = handlerFor(data, event);
      if (!handler) {
        return false;
      }
      if (data.phase !== phase) {
        return true;
      }
      if (handler(event) === true) {
        event.stopImmediatePropagation();
        return true;
      }
      return false;
    });
  };

  let bubbleContexts: { event: KeyboardEvent, ordered: ContextData[] } | null = null;

  const handleCapturePhase = (event: KeyboardEvent) => {
    const ordered = orderedContexts();
    bubbleContexts = { event, ordered };
    dispatch(event, 'capture', ordered);
  };

  const handleBubblePhase = (event: KeyboardEvent) => {
    const ordered = bubbleContexts?.event === event ? bubbleContexts.ordered : orderedContexts();
    bubbleContexts = null;
    dispatch(event, 'bubble', ordered);
  };

  window.addEventListener('keydown', handleCapturePhase, { capture: true });
  document.addEventListener('keydown', handleBubblePhase);

  const keyboard = function (context: string, config: KeyboardControllerConfig) {
    keyboard.registerHandlers(context, config);
  } as KeyboardControllerInstance;

  keyboard.registerHandlers = (context: string, { handlers = {}, focus, phase = 'capture' }) => {
    registrationCounter++;
    contexts.set(context, { handlers, registrationOrder: registrationCounter, focus, phase });
    activeContexts.add(context);
    focus && focusElement(context, focus);
  };

  keyboard.unregister = (context: string) => {
    contexts.delete(context);
    activeContexts.delete(context);
  };

  keyboard.focus = (context?: string) => {
    if (context && activeContexts.has(context)) {
      const contextData = contexts.get(context);
      contextData?.focus && focusElement(context, contextData.focus);
    }
  };

  keyboard.getFocus = (context: string) => (selector: string) => focusElement(context, selector);

  keyboard.clear = () => {
    contexts.clear();
    activeContexts.clear();
    focusAttempts.clear();
    bubbleContexts = null;
    registrationCounter = 0;
  };

  return keyboard;
};

const getKeyboard = () => {
  if ($keyboard) return $keyboard;
  $keyboard = createKeyboardController();
  return $keyboard;
};

export default {
  install(app) {
    app.prototype.$keyboard = getKeyboard();
  }
};

export function useKeyboard() {
  return { keyboard: getKeyboard() };
}
